// BHP-1088: shared VPS overview list. The generic / WordPress / Linked-Helper
// overviews are identical apart from (a) the `server-type` label they filter on
// and (b) the details route prefix, so they render this one component.
//
// Data comes from Backend2.0 `GET /api/vps` (customer-scoped by the backend).
// The VpsOutputDto carries only uuid/hostname/hetznerId/labels — no live Hetzner
// state (status, IP, created); those return with the Hetzner->Vps sync
// (BHP-1067). The card shows what the read-model has today.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useVpsSummaries } from '../application/vpsSummaryQueries';
import { editableLabels } from '../domain/vpsLabels';
import {
  filterByServerType,
  groupByProject,
  type VpsServerType,
  type VpsSummary
} from '../domain/vpsSummary';
import VpsCreateModal from './VpsCreateModal';

/** Shown for servers with no linked Hetzner project (BHP-1118). */
const UNKNOWN_PROJECT_LABEL = 'Unknown project';

/**
 * BHP-1115: the customer's own labels only.
 *
 * Used to hide just `server-type` — it names the very list you are looking at,
 * so repeating it on every card says nothing. The same holds for the rest of
 * what this platform sets: `customerUuid` is an internal identifier, and
 * `installation`, `ManagedBy` and `Client` are bookkeeping the customer never
 * wrote and cannot change (BHP-1114).
 */
function LabelChips({
  labels
}: {
  labels: Record<string, string>;
}): JSX.Element {
  const rows = editableLabels(labels);

  if (rows.length === 0) {
    return <span className="text-slate-400">No labels</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {rows.map((row) => (
        <span
          key={row.key}
          className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600"
        >
          {row.key}: {row.value}
        </span>
      ))}
    </div>
  );
}

function ServerCard({
  server,
  detailsBasePath
}: {
  server: VpsSummary;
  detailsBasePath: string;
}): JSX.Element {
  return (
    <div className="flex flex-col overflow-hidden rounded-xl border border-solid border-slate-200 bg-white shadow-sm">
      <div className="flex-1 p-4">
        <h3 className="text-base font-semibold text-slate-900">
          {server.hostname}
        </h3>
        <dl className="mt-3 space-y-2 text-sm text-slate-600">
          <div className="flex justify-between gap-3">
            <dt className="text-slate-400">Hetzner ID</dt>
            <dd className="font-medium text-slate-700">
              {server.hetznerId ?? '—'}
            </dd>
          </div>
          <div>
            <dt className="mb-1 text-slate-400">Labels</dt>
            <dd>
              <LabelChips labels={server.labels} />
            </dd>
          </div>
        </dl>
      </div>
      <div className="bg-slate-50 px-4 py-3">
        <Link
          to={`${detailsBasePath}/${server.uuid}`}
          className="inline-flex items-center rounded-md border-0 bg-teal-600 px-3 py-1.5 text-sm font-medium text-white no-underline hover:bg-teal-700"
        >
          Details
        </Link>
      </div>
    </div>
  );
}

function Spinner(): JSX.Element {
  return (
    <div
      role="status"
      aria-label="Loading"
      className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-solid border-slate-300 border-t-teal-600"
    />
  );
}

export interface VpsServerListProps {
  /** Which `server-type` label to show (generic / wordpress / linked-helper). */
  serverType: VpsServerType;
  /** Route prefix for the per-server Details link, e.g. `/vps/wordpress`. */
  detailsBasePath: string;
  /** Plural noun used in the heading and empty/error copy, e.g. "WordPress servers". */
  noun: string;
}

export default function VpsServerList({
  serverType,
  detailsBasePath,
  noun
}: VpsServerListProps): JSX.Element {
  const { data, isPending, isError } = useVpsSummaries();
  const [isCreating, setIsCreating] = useState(false);

  const servers =
    data !== undefined ? filterByServerType(data.items, serverType) : [];

  // BHP-1132: self-service creation is only wired for generic VPS so far;
  // WordPress and Linked Helper have their own flows (a separate story).
  const canCreate = serverType === 'generic';

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">{noun}</h2>
          <p className="text-sm text-slate-500">
            Products &amp; services overview
          </p>
        </div>
        {canCreate ? (
          <button
            type="button"
            onClick={() => {
              setIsCreating(true);
            }}
            className="shrink-0 rounded-md border-0 bg-teal-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-teal-700"
          >
            Create VPS
          </button>
        ) : null}
      </div>

      {isCreating ? (
        <VpsCreateModal
          onClose={() => {
            setIsCreating(false);
          }}
        />
      ) : null}

      {isPending ? (
        <div className="py-12">
          <Spinner />
        </div>
      ) : isError ? (
        <div className="rounded-lg border border-solid border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          Could not load {noun}. Please try again.
        </div>
      ) : servers.length === 0 ? (
        <div className="rounded-xl border border-solid border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          No {noun} for this customer.
        </div>
      ) : (
        <div className="space-y-8">
          {groupByProject(servers).map((group) => (
            <section key={group.projectName ?? '__unknown__'}>
              {/* BHP-1118: a heading per Hetzner project so servers from
                  different projects are not shown as one undifferentiated list. */}
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
                {group.projectName ?? UNKNOWN_PROJECT_LABEL}
              </h3>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {group.servers.map((server) => (
                  <ServerCard
                    key={server.uuid}
                    server={server}
                    detailsBasePath={detailsBasePath}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
